import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../../../auth/api";
import {
  HiArrowLeft,
  HiPhotograph,
  HiVideoCamera,
  HiUpload,
  HiLink,
  HiInformationCircle,
} from "react-icons/hi";

function CreateGallery() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    media_type: "image",
    format_type: "16:9",
    category: "General",
    url_input: "",
    is_active: true,
  });

  const [uploadMode, setUploadMode] = useState("file"); // 'file' or 'url'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    "General",
    "Roof Waterproofing",
    "Basement Waterproofing",
    "Bathroom & Wet Areas",
    "Commercial Projects",
    "Residential Projects",
    "Chemical Application",
    "Inspection & Testing",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const oversized = files.find((f) => f.size > 15 * 1024 * 1024);
      if (oversized) {
        toast.error(`File ${oversized.name} exceeds 15MB limit`);
        return;
      }
      setSelectedFiles(files);
      setPreviewUrls(files.map((file) => URL.createObjectURL(file)));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Please enter a title (or base title for multiple items)");
      return;
    }

    if (
      formData.media_type === "image" &&
      uploadMode === "file" &&
      selectedFiles.length === 0
    ) {
      toast.error("Please select at least one image file to upload");
      return;
    }

    if (
      (formData.media_type === "video" || uploadMode === "url") &&
      !formData.url_input.trim()
    ) {
      toast.error("Please enter at least one valid URL link");
      return;
    }

    setIsSubmitting(true);

    try {
      if (
        formData.media_type === "image" &&
        uploadMode === "file" &&
        selectedFiles.length > 0
      ) {
        const bodyFormData = new FormData();
        bodyFormData.append("title", formData.title);
        bodyFormData.append("description", formData.description);
        bodyFormData.append("media_type", formData.media_type);
        bodyFormData.append("format_type", formData.format_type);
        bodyFormData.append("category", formData.category);
        bodyFormData.append("is_active", formData.is_active);

        selectedFiles.forEach((file) => {
          bodyFormData.append("image", file);
        });

        const res = await fetchWithAuth("POST", "createGallery", bodyFormData, true);
        toast.success(res.data.message || "Gallery items uploaded successfully");
      } else {
        const res = await fetchWithAuth("POST", "createGallery", {
          ...formData,
          urls_input: formData.url_input.trim(),
        });
        toast.success(res.data.message || "Gallery items created successfully");
      }

      navigate("/admin/dashboard/gallery");
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to create gallery item(s)";
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12">
      {/* Back Button & Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/dashboard/gallery"
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors"
        >
          <HiArrowLeft size={16} /> Back to Gallery
        </Link>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl overflow-hidden">
        <div className="bg-slate-900 px-8 py-6 text-white flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-black tracking-tight">
              Add New Gallery Item
            </h1>
            <p className="text-xs text-slate-400 font-medium mt-1">
              Upload photos to Cloudinary or embed video links (YouTube, Shorts, TikTok, Facebook).
            </p>
          </div>
          <div className="w-12 h-12 bg-blue-600/20 border border-blue-500/30 text-blue-400 rounded-2xl flex items-center justify-center">
            {formData.media_type === "image" ? (
              <HiPhotograph size={24} />
            ) : (
              <HiVideoCamera size={24} />
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Media Type Selector */}
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-3">
              1. Media Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, media_type: "image" }))}
                className={`p-4 rounded-2xl border-2 flex items-center justify-center gap-3 font-bold text-sm transition-all ${
                  formData.media_type === "image"
                    ? "border-blue-600 bg-blue-50/50 text-blue-600 shadow-md"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <HiPhotograph size={20} /> Photo
              </button>
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, media_type: "video" }))}
                className={`p-4 rounded-2xl border-2 flex items-center justify-center gap-3 font-bold text-sm transition-all ${
                  formData.media_type === "video"
                    ? "border-purple-600 bg-purple-50/50 text-purple-600 shadow-md"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <HiVideoCamera size={20} /> Video Link
              </button>
            </div>
          </div>

          {/* Aspect Ratio / Format Type Selector */}
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-3">
              2. Aspect Ratio / Format Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label
                className={`p-4 rounded-2xl border-2 cursor-pointer flex flex-col justify-center gap-1 transition-all ${
                  formData.format_type === "16:9"
                    ? "border-blue-600 bg-blue-50/30 text-slate-900"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="format_type"
                    value="16:9"
                    checked={formData.format_type === "16:9"}
                    onChange={handleChange}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-bold text-sm">16:9 Widescreen / Landscape</span>
                </div>
                <span className="text-[11px] text-slate-500 pl-6">
                  Standard horizontal videos, YouTube full videos, landscape photos.
                </span>
              </label>

              <label
                className={`p-4 rounded-2xl border-2 cursor-pointer flex flex-col justify-center gap-1 transition-all ${
                  formData.format_type === "9:16"
                    ? "border-purple-600 bg-purple-50/30 text-slate-900"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="format_type"
                    value="9:16"
                    checked={formData.format_type === "9:16"}
                    onChange={handleChange}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span className="font-bold text-sm">9:16 Portrait / Shorts / Reels</span>
                </div>
                <span className="text-[11px] text-slate-500 pl-6">
                  Vertical videos, YouTube Shorts, TikToks, Instagram & Facebook Reels.
                </span>
              </label>
            </div>
          </div>

          {/* Media Input (Upload or Link) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-black uppercase tracking-widest text-slate-700">
                3. {formData.media_type === "image" ? "Photo Input" : "Video Link"}
              </label>

              {formData.media_type === "image" && (
                <div className="flex gap-2 bg-slate-100 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setUploadMode("file")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      uploadMode === "file"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode("url")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      uploadMode === "url"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    Paste Image URL
                  </button>
                </div>
              )}
            </div>

            {formData.media_type === "image" && uploadMode === "file" ? (
              <div className="border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-2xl p-8 text-center bg-slate-50 transition-colors relative">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                    <HiUpload size={24} />
                  </div>
                  <p className="text-sm font-bold text-slate-700">
                    {selectedFiles.length > 0
                      ? `${selectedFiles.length} file(s) selected for upload`
                      : "Click or drag multiple image files here"}
                  </p>
                  <p className="text-xs text-slate-400">
                    Uploads automatically to Cloudinary (Select multiple PNG, JPG, WEBP files up to 15MB each)
                  </p>
                </div>

                {previewUrls.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 max-h-48 overflow-y-auto p-2 bg-white rounded-xl border border-slate-200">
                    {previewUrls.map((url, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                        <img src={url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <textarea
                  name="url_input"
                  rows={4}
                  value={formData.url_input}
                  onChange={handleChange}
                  placeholder={
                    formData.media_type === "image"
                      ? "Paste image URLs here (one URL per line):\nhttps://res.cloudinary.com/.../photo1.png\nhttps://res.cloudinary.com/.../photo2.png"
                      : "Paste video links here (one link per line):\nhttps://www.youtube.com/watch?v=...\nhttps://youtube.com/shorts/...\nhttps://www.tiktok.com/@.../video/..."
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs sm:text-sm font-mono focus:outline-none focus:border-blue-500"
                ></textarea>
                {formData.media_type === "video" && (
                  <div className="flex items-start gap-2 mt-2 text-xs text-slate-500">
                    <HiInformationCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={16} />
                    <span>
                      Paste multiple YouTube videos, YouTube Shorts, TikToks, Facebook Reels, or Instagram Reels (one link per line).
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Title & Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-2">
                4. Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. Commercial Roof Waterproofing Treatment"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-2">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:outline-none focus:border-blue-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief details about this photo or video..."
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-blue-500"
            ></textarea>
          </div>

          {/* Status Checkbox */}
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="text-sm font-bold text-slate-800 cursor-pointer">
              Publish immediately (Active in public Gallery)
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-slate-100">
            <Link
              to="/admin/dashboard/gallery"
              className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs sm:text-sm transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs sm:text-sm transition-all shadow-lg shadow-blue-500/20 active:scale-95 disabled:opacity-50"
            >
              {isSubmitting ? "Creating..." : "Save Gallery Item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateGallery;
